import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { authApi } from '@services/api'
import { KeyRound, CheckCircle, AlertCircle, Eye, EyeOff, Loader2, XCircle, Mail, Send } from 'lucide-react'

interface SetupForm {
  new_password: string
  confirm_password: string
}

interface TokenInfo {
  email: string
  first_name: string | null
  last_name: string | null
}

type PageState = 'loading' | 'form' | 'success' | 'expired' | 'invalid'

export default function PasswordSetupPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [pageState, setPageState] = useState<PageState>('loading')
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SetupForm>()
  const password = watch('new_password', '')

  // Password strength checks
  const hasMinLength = password.length >= 8
  const hasUppercase = /[A-Z]/.test(password)
  const hasLowercase = /[a-z]/.test(password)
  const hasDigit = /\d/.test(password)
  const allMet = hasMinLength && hasUppercase && hasLowercase && hasDigit

  useEffect(() => {
    if (!token) {
      setPageState('invalid')
      return
    }

    authApi.validatePasswordToken(token)
      .then((res) => {
        setTokenInfo(res.data)
        setPageState('form')
      })
      .catch((err) => {
        const code = err.response?.data?.detail?.code
        if (code === 'TOKEN_EXPIRED') {
          setPageState('expired')
        } else {
          setPageState('invalid')
        }
      })
  }, [token])

  const onSubmit = async (data: SetupForm) => {
    if (!token) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await authApi.completePasswordSetup({ token, new_password: data.new_password })
      setPageState('success')
      setTimeout(() => navigate('/login'), 3000)
    } catch (err: unknown) {
      const error = err as { response?: { status?: number } }
      if (error.response?.status === 422) {
        setSubmitError(t('auth.passwordSetup.weakPassword'))
      } else {
        setSubmitError(t('auth.passwordSetup.error'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
          <Loader2 size={32} className="text-primary-600 animate-spin" />
        </div>
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    )
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.passwordSetup.successTitle')}</h2>
        <p className="text-gray-600">{t('auth.passwordSetup.successMessage')}</p>
      </div>
    )
  }

  // Expired or invalid token — offer magic link
  if (pageState === 'expired' || pageState === 'invalid') {
    return <ExpiredTokenView />
  }

  // Form state
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound size={32} className="text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.passwordSetup.title')}</h2>
        <p className="text-gray-600 mt-1">{t('auth.passwordSetup.subtitle')}</p>
      </div>

      {tokenInfo && (
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500">{t('auth.passwordSetup.settingUpFor')}</p>
          <p className="font-medium text-gray-900">
            {tokenInfo.first_name && tokenInfo.last_name
              ? `${tokenInfo.first_name} ${tokenInfo.last_name}`
              : tokenInfo.email}
          </p>
          {tokenInfo.first_name && <p className="text-sm text-gray-500">{tokenInfo.email}</p>}
        </div>
      )}

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <form data-testid="auth-password-setup-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('auth.passwordSetup.newPassword')}
          </label>
          <div className="relative">
            <input
              data-testid="auth-password-setup-new-password-input"
              type={showPassword ? 'text' : 'password'}
              className="input pr-10"
              {...register('new_password', {
                required: t('validation.required'),
                validate: () => allMet || t('auth.passwordSetup.requirementsNotMet'),
              })}
            />
            <button
              data-testid="auth-password-setup-password-btn"
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.new_password && (
            <p className="text-sm text-red-600 mt-1">{errors.new_password.message}</p>
          )}

          {/* Password strength indicator */}
          {password.length > 0 && (
            <div className="mt-2 space-y-1">
              <PasswordCheck met={hasMinLength} label={t('auth.passwordSetup.reqMinLength')} />
              <PasswordCheck met={hasUppercase} label={t('auth.passwordSetup.reqUppercase')} />
              <PasswordCheck met={hasLowercase} label={t('auth.passwordSetup.reqLowercase')} />
              <PasswordCheck met={hasDigit} label={t('auth.passwordSetup.reqDigit')} />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('auth.passwordSetup.confirmPassword')}
          </label>
          <div className="relative">
            <input
              data-testid="auth-password-setup-confirm-password-input"
              type={showConfirm ? 'text' : 'password'}
              className="input pr-10"
              {...register('confirm_password', {
                required: t('validation.required'),
                validate: (value) =>
                  value === password || t('validation.passwordMismatch'),
              })}
            />
            <button
              data-testid="auth-password-setup-confirm-btn"
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowConfirm(!showConfirm)}
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirm_password && (
            <p className="text-sm text-red-600 mt-1">{errors.confirm_password.message}</p>
          )}
        </div>

        <button
          data-testid="auth-password-setup-submit-btn"
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {isSubmitting && <Loader2 size={18} className="animate-spin" />}
          {isSubmitting ? t('common.loading') : t('auth.passwordSetup.submit')}
        </button>
      </form>
    </div>
  )
}

function ExpiredTokenView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleRequestMagicLink = async () => {
    if (!email) return
    setSending(true)
    setError(null)
    try {
      await authApi.requestMagicLink(email)
      setSent(true)
    } catch {
      setError(t('auth.passwordSetup.magicLinkError', 'Fehler beim Senden. Bitte versuchen Sie es erneut.'))
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Send size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t('auth.passwordSetup.magicLinkSentTitle', 'Link gesendet!')}
        </h2>
        <p className="text-gray-600">
          {t('auth.passwordSetup.magicLinkSentMessage', 'Wir haben Ihnen einen neuen Zugangslink an {{email}} gesendet. Bitte prüfen Sie Ihren Posteingang.').replace('{{email}}', email)}
        </p>
        <p className="text-sm text-gray-400">
          {t('auth.passwordSetup.magicLinkSentHint', 'Der Link ist 15 Minuten gültig. Schauen Sie auch im Spam-Ordner nach.')}
        </p>
      </div>
    )
  }

  return (
    <div className="text-center space-y-5">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
        <AlertCircle size={32} className="text-amber-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t('auth.passwordSetup.expiredTitle')}
        </h2>
        <p className="text-gray-600 mt-2">
          {t('auth.passwordSetup.expiredMessageNew', 'Dieser Link ist leider abgelaufen. Kein Problem — geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen neuen Zugangslink.')}
        </p>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="auth-password-setup-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.passwordSetup.emailPlaceholder', 'Ihre E-Mail-Adresse')}
            className="input pl-10 w-full"
            onKeyDown={(e) => e.key === 'Enter' && handleRequestMagicLink()}
          />
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <button
          data-testid="auth-password-setup-request-magic-link-btn"
          onClick={handleRequestMagicLink}
          disabled={!email || sending}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {sending ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Send size={18} />
          )}
          {sending
            ? t('common.loading')
            : t('auth.passwordSetup.requestNewLink', 'Neuen Zugangslink senden')}
        </button>
      </div>

      <div className="pt-2 border-t">
        <button
          data-testid="auth-password-setup-back-to-login-btn"
          onClick={() => navigate('/login')}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {t('auth.passwordSetup.backToLogin', 'Zurück zur Anmeldung')}
        </button>
      </div>
    </div>
  )
}

function PasswordCheck({ met, label }: { met: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      {met ? <CheckCircle size={14} /> : <XCircle size={14} />}
      <span>{label}</span>
    </div>
  )
}
